import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import {
  Clock9,
  MapPin,
  Calendar,
  Plus,
  CheckCircle2,
  AlertCircle,
  XCircle,
  Clock,
  SlidersHorizontal as Filters,
  X,
  Search,
  Loader2,
  MapPinned,
  ChevronDown,
  ChevronUp,
  LayoutDashboard
} from 'lucide-react';
import Sidebar from '../../../components/Dashboard/Sidebar/Sidebar';
import Header from '../../../components/Dashboard/Header/Header';
import Breadcrumbs from '../../../components/Common/Breadcrumbs/Breadcrumbs';
import Footer from '../../../components/Common/Footer/Footer';
import CustomSelect from '../../../components/Common/Select/CustomSelect';
import Pagination from '../../../components/Common/Pagination/Pagination';
import OTRequestDrawer from './OTRequestDrawer';
import api from '../../../api/axios';
import toast from 'react-hot-toast';
import styles from './Overtime.module.css';

const EmployeeOT = () => {
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const initialTab = queryParams.get('tab') === 'requests' ? 'requests' : 'assignments';
  const initialSearch = queryParams.get('search') || '';

  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [isSummaryOpen, setIsSummaryOpen] = useState(false);
  const [activeTab, setActiveTab] = useState(initialTab); // 'assignments' or 'requests'
  const [searchTerm, setSearchTerm] = useState(initialSearch);
  const [loading, setLoading] = useState(false);
  const [punchingId, setPunchingId] = useState(null);
  const [isRequestDrawerOpen, setIsRequestDrawerOpen] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [paginationData, setPaginationData] = useState({
    current_page: 1,
    total: 0,
    per_page: 15,
    last_page: 1
  });

  const [assignments, setAssignments] = useState([]);
  const [requests, setRequests] = useState([]);
  const [employeeSummary, setEmployeeSummary] = useState({
    totalHours: 0,
    totalDays: 0,
    validCount: 0,
    pendingRequests: 0
  });

  const [filters, setFilters] = useState({
    site: null,
    date: '',
    status: null
  });

  const [siteOptions, setSiteOptions] = useState([]);
  const [showFilters, setShowFilters] = useState(false);

  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const employeeId = user.employee_id || user.id;

  // Fetch projects list for filter dropdown
  useEffect(() => {
    const fetchSites = async () => {
      try {
        const response = await api.get('/projects-dropdown');
        if (response.data && response.data.data) {
          setSiteOptions(response.data.data.map(proj => ({
            value: proj.id,
            label: proj.project_name
          })));
        } else if (response.data) {
          setSiteOptions(response.data.map(proj => ({
            value: proj.id,
            label: proj.project_name
          })));
        }
      } catch (error) {
        console.error('Failed to load project site options for filter:', error);
      }
    };
    fetchSites();
  }, []);

  // Sync tab active state and search term with URL query changes
  useEffect(() => {
    const tab = queryParams.get('tab') === 'requests' ? 'requests' : 'assignments';
    setActiveTab(tab);
    const search = queryParams.get('search') || '';
    setSearchTerm(search);
  }, [location.search]);

  const fetchAssignments = async (page = 1) => {
    setLoading(true);
    try {
      const params = {
        page,
        per_page: paginationData.per_page,
      };
      if (filters.status) {
        params.status = filters.status;
      }
      if (filters.date) {
        params.from_date = filters.date;
        params.to_date = filters.date;
      }
      if (filters.site) {
        params.project_id = filters.site;
      }
      if (searchTerm) {
        params.search = searchTerm;
      }
      const response = await api.get('/overtime/my-assignments', { params });
      if (response.data && response.data.success) {
        const paginatedData = response.data.data;
        const rawList = paginatedData.data || paginatedData || [];
        const mapped = rawList.map(item => ({
          id: item.id,
          employee: user.name || 'Me',
          employee_id: item.employee_id,
          project_site: item.project ? item.project.project_name : 'N/A',
          project_site_id: item.project_id,
          date: item.ot_date ? new Date(item.ot_date).toISOString().split('T')[0] : '',
          assigned_by: item.assigned_by ? (item.assigned_by.name || 'Admin') : 'Admin',
          expected_start_time: item.expected_start_time
            ? new Date(item.expected_start_time).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: false })
            : '18:00',
          expected_end_time: item.expected_end_time
            ? new Date(item.expected_end_time).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: false })
            : '22:00',
          instructions: item.assignment_reason || 'N/A',
          status: item.status || 'assigned',
          punch_in_at: item.punch_in_at,
          punch_out_at: item.punch_out_at,
          ot_hours: item.ot_hours
        }));
        setAssignments(mapped);

        if (paginatedData.current_page) {
          setPaginationData({
            current_page: paginatedData.current_page,
            total: paginatedData.total,
            per_page: paginatedData.per_page,
            last_page: paginatedData.last_page
          });
        }

        if (response.data.summary) {
          setEmployeeSummary(prev => ({
            ...prev,
            totalHours: response.data.summary.total_hours || 0,
            totalDays: response.data.summary.total_days || 0,
            validCount: response.data.summary.valid_ot_count || 0,
            pendingRequests: response.data.summary.pending_requests || 0
          }));
        }
      }
    } catch (error) {
      console.error('Failed to load employee overtime assignments:', error);
      toast.error('Failed to load overtime assignments');
    } finally {
      setLoading(false);
    }
  };

  const fetchRequests = async (page = 1) => {
    setLoading(true);
    try {
      const params = {
        page,
        per_page: paginationData.per_page,
      };
      if (filters.status) {
        params.status = filters.status;
      }
      if (filters.date) {
        params.from_date = filters.date;
        params.to_date = filters.date;
      }
      if (filters.site) {
        params.project_id = filters.site;
      }
      if (searchTerm) {
        params.search = searchTerm;
      }
      const response = await api.get(`/overtime/my-assignments`, { params });
      if (response.data && response.data.success) {
        const paginatedData = response.data.data;
        const rawList = paginatedData.data || paginatedData || [];
        const mapped = rawList.map(item => ({
          id: item.id,
          project_site: item.project ? item.project.project_name : 'N/A',
          project_site_id: item.project_id,
          date: item.request_date || item.ot_date || '',
          requested_start_time: item.requested_start_time ? item.requested_start_time.substring(0, 5) : '18:00',
          requested_end_time: item.requested_end_time ? item.requested_end_time.substring(0, 5) : '22:00',
          reason: item.reason || item.assignment_reason || 'N/A',
          status: item.status || 'pending',
          approval_remarks: item.approval_remarks || ''
        }));
        setRequests(mapped);

        if (paginatedData.current_page) {
          setPaginationData({
            current_page: paginatedData.current_page,
            total: paginatedData.total,
            per_page: paginatedData.per_page,
            last_page: paginatedData.last_page
          });
        }

        // Update pending requests count dynamically
        const pendingCount = mapped.filter(r => r.status === 'pending').length;
        setEmployeeSummary(prev => ({
          ...prev,
          pendingRequests: pendingCount
        }));
      }
    } catch (error) {
      console.error('Failed to load employee overtime requests:', error);
      toast.error('Failed to load overtime requests');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    setCurrentPage(1);
  }, [activeTab, filters, searchTerm]);

  useEffect(() => {
    if (activeTab === 'assignments') {
      fetchAssignments(currentPage);
    } else {
      fetchRequests(currentPage);
    }
  }, [currentPage, activeTab, filters, searchTerm]);

  const handlePunchAction = async (record, action) => {
    if (!navigator.geolocation) {
      toast.error('Geolocation is not supported by your browser');
      return;
    }

    setPunchingId(record.id);
    navigator.geolocation.getCurrentPosition(
      async (position) => {
        try {
          const payload = {
            assignment_id: record.id,
            latitude: String(position.coords.latitude),
            longitude: String(position.coords.longitude),
            address: 'Punched via Employee Mobile Portal'
          };

          const endpoint = action === 'in' ? '/overtime/punch-in' : '/overtime/punch-out';
          const response = await api.post(endpoint, payload);

          if (response.data && response.data.success) {
            toast.success(action === 'in' ? 'OT Punch In Successful!' : 'OT Punch Out Successful!');
            fetchAssignments();
          } else {
            toast.error(response.data?.message || 'Punch operation failed');
          }
        } catch (error) {
          console.error(error);
          toast.error(error.response?.data?.message || 'Failed to register punch action');
        } finally {
          setPunchingId(null);
        }
      },
      (error) => {
        console.error('Location error:', error);
        toast.error('Location access denied. Please enable GPS to punch.');
        setPunchingId(null);
      },
      { enableHighAccuracy: true }
    );
  };

  // Convert "HH:mm" (24-hr) string to "h:mm AM/PM" (12-hr) format
  const to12hr = (timeStr) => {
    if (!timeStr) return '—';
    const [hourStr, minuteStr] = timeStr.split(':');
    let hour = parseInt(hourStr, 10);
    const minute = minuteStr || '00';
    const ampm = hour >= 12 ? 'PM' : 'AM';
    hour = hour % 12 || 12;
    return `${hour}:${minute} ${ampm}`;
  };

  const getStatusClass = (status) => {
    const s = status?.toLowerCase();
    if (s === 'approved' || s === 'completed') return styles['status-approved'];
    if (s === 'rejected' || s === 'cancelled') return styles['status-rejected'];
    return styles['status-pending'];
  };

  const getStatusLabel = (status) => {
    const s = status?.toLowerCase();
    if (s === 'assigned') return 'Assigned';
    if (s === 'in_progress') return 'In Progress';
    if (s === 'completed') return 'Completed';
    if (s === 'cancelled') return 'Cancelled';
    if (s === 'approved') return 'Approved';
    if (s === 'rejected') return 'Rejected';
    return 'Pending';
  };

  const getStatusIcon = (status) => {
    const s = status?.toLowerCase();
    if (s === 'approved' || s === 'completed') return <CheckCircle2 size={12} />;
    if (s === 'rejected' || s === 'cancelled') return <XCircle size={12} />;
    return <AlertCircle size={12} />;
  };

  const hasActiveFilters = !!(filters.site || filters.date || filters.status);

  // Filters logic
  const filteredAssignments = assignments.filter(record => {
    const matchesSearch =
      (record.project_site && record.project_site.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (record.instructions && record.instructions.toLowerCase().includes(searchTerm.toLowerCase())) ||
      String(record.id) === searchTerm;

    const matchesSite = !filters.site || Number(record.project_site_id) === Number(filters.site);
    const matchesDate = !filters.date || record.date === filters.date;

    return matchesSearch && matchesSite && matchesDate;
  });

  const filteredRequests = requests.filter(record => {
    const matchesSearch =
      (record.project_site && record.project_site.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (record.reason && record.reason.toLowerCase().includes(searchTerm.toLowerCase())) ||
      String(record.id) === searchTerm;

    const matchesSite = !filters.site || Number(record.project_site_id) === Number(filters.site);
    const matchesDate = !filters.date || record.date === filters.date;
    const matchesStatus = !filters.status || record.status === filters.status;

    return matchesSearch && matchesSite && matchesDate && matchesStatus;
  });

  const summaryData = [
    {
      icon: <Clock9 size={20} />,
      label: 'OT Hours (MTD)',
      value: String(employeeSummary.totalHours),
      theme: 'blue'
    },
    {
      icon: <CheckCircle2 size={20} />,
      label: 'Valid OT Shifts',
      value: String(employeeSummary.validCount).padStart(2, '0'),
      theme: 'green'
    },
    {
      icon: <AlertCircle size={20} />,
      label: 'Pending Requests',
      value: String(employeeSummary.pendingRequests).padStart(2, '0'),
      theme: 'orange'
    }
  ];

  return (
    <div className={styles['overtime-container']}>
      <Sidebar isOpen={isSidebarOpen} onClose={() => setIsSidebarOpen(false)} />
      {isSidebarOpen && <div className={styles['sidebar-overlay']} onClick={() => setIsSidebarOpen(false)}></div>}

      <div className={styles['content-wrapper']}>
        <Header onMenuClick={() => setIsSidebarOpen(true)} />

        <main className={styles['main-content']}>
          <Breadcrumbs customItems={[
            { label: 'Employee Dashboard', path: '/employees/dashboard' },
            { label: 'My Overtime', path: '/employees/my-overtime' }
          ]} />

          <div className={styles['page-header']}>
            <div className={styles['header-left']}>
              <h1>My Overtime</h1>
              <p>View your scheduled overtime shifts, track hours, and raise new requests.</p>
            </div>
            <div className={styles['header-actions']}>
              <button className={styles['add-btn']} onClick={() => setIsRequestDrawerOpen(true)}>
                <Plus size={18} />
                <span>Request Overtime</span>
              </button>
            </div>
          </div>

          <div className={styles['summary-collapsible']}>
            <button
              className={styles['summary-toggle']}
              onClick={() => setIsSummaryOpen(prev => !prev)}
            >
              <div className={styles['summary-toggle-left']}>
                <LayoutDashboard size={20} style={{ color: '#f97316' }} />
                <div className={styles['summary-toggle-text']}>
                  <span className={styles['summary-toggle-title']}>Summary</span>
                  <span className={styles['summary-toggle-hint']}>
                    {summaryData[0]?.value || 0} Hours · {summaryData[2]?.value || '00'} pending
                  </span>
                </div>
              </div>
              {isSummaryOpen ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
            </button>

            <div className={`${styles['summary-metrics-row']} ${isSummaryOpen ? styles['summary-expanded'] : styles['summary-collapsed']}`}>
              {summaryData.map((m, i) => (
                <div key={i} className={`${styles['metric-card']} ${styles[m.theme]}`}>
                  <div className={styles['metric-icon-box']}>
                    {m.icon}
                  </div>
                  <div className={styles['metric-info']}>
                    <span className={styles['metric-label']}>{m.label}</span>
                    <div className={styles['metric-value-group']}>
                      <span className={styles['metric-value']}>{m.value}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Tabs Navigation */}
          <div className={styles['tabs-nav']}>
            <button
              className={`${styles['tab-btn']} ${activeTab === 'assignments' ? styles['active'] : ''}`}
              onClick={() => setActiveTab('assignments')}
            >
              <Clock size={18} />
              My Assignments
            </button>
            <button
              className={`${styles['tab-btn']} ${activeTab === 'requests' ? styles['active'] : ''}`}
              onClick={() => setActiveTab('requests')}
            >
              <Calendar size={18} />
              My Requests
            </button>
          </div>

          {/* Table Controls */}
          <div className={styles['table-controls']}>
            <div className={styles['search-wrapper']}>
              <Search className={styles['search-icon']} size={20} />
              <input
                type="text"
                placeholder={activeTab === 'assignments' ? "Search assignments..." : "Search requests..."}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className={styles['search-input']}
              />
            </div>
            <div className={styles['filter-actions']}>
              <button
                className={`${styles['filter-toggle']} ${hasActiveFilters ? styles['active'] : ''}`}
                onClick={(e) => { e.stopPropagation(); setShowFilters(!showFilters); }}
              >
                <Filters size={18} />
                <span>Filters</span>
                {hasActiveFilters && <span className={styles['filter-badge']} />}
              </button>
            </div>
          </div>

          {showFilters && (
            <div className={styles['filters-panel']} onClick={(e) => e.stopPropagation()}>
              <div className={styles['filters-grid']}>
                <div className={styles['filter-group']}>
                  <label>Project Site</label>
                  <CustomSelect
                    options={siteOptions}
                    value={siteOptions.find(s => s.value === filters.site)}
                    onChange={(opt) => setFilters({ ...filters, site: opt ? opt.value : null })}
                    placeholder="All Sites"
                    isClearable
                  />
                </div>

                <div className={styles['filter-group']}>
                  <label>Date</label>
                  <input
                    type="date"
                    className={styles['date-input']}
                    value={filters.date}
                    onChange={(e) => setFilters({ ...filters, date: e.target.value })}
                  />
                </div>

                {activeTab === 'requests' && (
                  <div className={styles['filter-group']}>
                    <label>Status</label>
                    <CustomSelect
                      options={[
                        { value: 'pending', label: 'Pending' },
                        { value: 'approved', label: 'Approved' },
                        { value: 'rejected', label: 'Rejected' }
                      ]}
                      value={[
                        { value: 'pending', label: 'Pending' },
                        { value: 'approved', label: 'Approved' },
                        { value: 'rejected', label: 'Rejected' }
                      ].find(o => o.value === filters.status)}
                      onChange={(opt) => setFilters({ ...filters, status: opt ? opt.value : null })}
                      placeholder="All Status"
                      isClearable
                    />
                  </div>
                )}
              </div>
              <div className={styles['filters-footer']}>
                <button
                  className={styles['reset-btn']}
                  onClick={() => setFilters({ site: null, date: '', status: null })}
                >
                  Reset Filters
                </button>
                <button
                  className={styles['close-filters-btn']}
                  onClick={() => setShowFilters(false)}
                >
                  Close
                </button>
              </div>
            </div>
          )}

          {/* Data List Section */}
          <div className={styles['data-view-container']}>
            {loading ? (
              <div className={styles['loading-container']}>
                <Loader2 className={styles['spinner']} size={36} />
                <p>Loading overtime details...</p>
              </div>
            ) : activeTab === 'assignments' ? (
              <div className={styles['table-responsive']}>
                <table className={styles['data-table']}>
                  <thead>
                    <tr>
                      <th>Shift Date</th>
                      <th>Project Site</th>
                      <th>Scheduled Hours</th>
                      <th>Reason / Instructions</th>
                      <th>Punches</th>
                      <th>OT Hours</th>
                      <th>Status</th>
                      <th>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredAssignments.length === 0 ? (
                      <tr>
                        <td colSpan="8" className={styles['no-data-cell']} style={{ textAlign: 'center', padding: '40px' }}>
                          <Clock9 size={48} style={{ color: '#94a3b8', marginBottom: '12px' }} />
                          <p>No overtime assignments scheduled.</p>
                        </td>
                      </tr>
                    ) : (
                      filteredAssignments.map((assignment) => (
                        <tr key={assignment.id}>
                          <td style={{ whiteSpace: 'nowrap', fontWeight: 'bold' }}>
                            {assignment.date ? new Date(assignment.date).toLocaleDateString('en-US', { day: '2-digit', month: 'short', year: 'numeric' }) : 'N/A'}
                          </td>
                          <td>
                            <div className={styles['project-cell']}>
                              <span className={styles['project-name']}>{assignment.project_site}</span>
                              <span className={styles['assigned-by']}>Assigned by: {assignment.assigned_by}</span>
                            </div>
                          </td>
                          <td style={{ whiteSpace: 'nowrap' }}>
                            {to12hr(assignment.expected_start_time)} – {to12hr(assignment.expected_end_time)}
                          </td>
                          <td style={{ fontSize: '13px', color: '#64748b', fontStyle: 'italic', maxWidth: '200px' }}>
                            {assignment.instructions && assignment.instructions !== 'N/A' ? `"${assignment.instructions}"` : '—'}
                          </td>
                          <td>
                            <div className={styles['punch-times']}>
                              <div>In: {assignment.punch_in_at ? new Date(assignment.punch_in_at).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit', hour12: true }) : '—'}</div>
                              <div>Out: {assignment.punch_out_at ? new Date(assignment.punch_out_at).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit', hour12: true }) : '—'}</div>
                            </div>
                          </td>
                          <td style={{ fontWeight: 'bold', color: '#1e293b' }}>
                            {assignment.ot_hours !== null && assignment.ot_hours !== undefined ? `${assignment.ot_hours} hrs` : '—'}
                          </td>
                          <td>
                            <span className={`${styles['status-badge']} ${getStatusClass(assignment.status)}`}>
                              {getStatusIcon(assignment.status)}
                              <span>{getStatusLabel(assignment.status)}</span>
                            </span>
                          </td>
                          <td>
                            {assignment.status?.toLowerCase() === 'assigned' && (
                              <button
                                className={styles['punch-btn-success']}
                                onClick={() => handlePunchAction(assignment, 'in')}
                                disabled={punchingId === assignment.id}
                              >
                                {punchingId === assignment.id ? <Loader2 className={styles['spinner']} size={14} /> : <MapPinned size={14} />}
                                <span>Punch In</span>
                              </button>
                            )}

                            {assignment.status?.toLowerCase() === 'in_progress' && (
                              <button
                                className={styles['punch-btn-danger']}
                                onClick={() => handlePunchAction(assignment, 'out')}
                                disabled={punchingId === assignment.id}
                              >
                                {punchingId === assignment.id ? <Loader2 className={styles['spinner']} size={14} /> : <MapPinned size={14} />}
                                <span>Punch Out</span>
                              </button>
                            )}

                            {assignment.status?.toLowerCase() === 'completed' && (
                              <span className={styles['punch-completed-badge']}>
                                <CheckCircle2 size={14} />
                                <span>Completed</span>
                              </span>
                            )}
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className={styles['table-responsive']}>
                <table className={styles['data-table']}>
                  <thead>
                    <tr>
                      <th>Request Date</th>
                      <th>Project Site</th>
                      <th>Requested Timings</th>
                      <th>Reason</th>
                      <th>Status</th>
                      <th>Review Remarks</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredRequests.length === 0 ? (
                      <tr>
                        <td colSpan="6" className={styles['no-data-cell']} style={{ textAlign: 'center', padding: '40px' }}>
                          <Calendar size={48} style={{ color: '#94a3b8', marginBottom: '12px' }} />
                          <p>No overtime requests raised.</p>
                        </td>
                      </tr>
                    ) : (
                      filteredRequests.map((req) => (
                        <tr key={req.id}>
                          <td style={{ whiteSpace: 'nowrap', fontWeight: 'bold' }}>
                            {req.date ? new Date(req.date).toLocaleDateString('en-US', { day: '2-digit', month: 'short', year: 'numeric' }) : 'N/A'}
                          </td>
                          <td>{req.project_site}</td>
                          <td style={{ whiteSpace: 'nowrap' }}>
                            {req.requested_start_time} - {req.requested_end_time}
                          </td>
                          <td>{req.reason}</td>
                          <td>
                            <span className={`${styles['status-badge']} ${getStatusClass(req.status)}`}>
                              {getStatusIcon(req.status)}
                              <span>{getStatusLabel(req.status)}</span>
                            </span>
                          </td>
                          <td>
                            <span style={{ fontSize: '13px', color: '#64748b' }}>
                              {req.approval_remarks || '—'}
                            </span>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            )}

            {/* Mobile View */}
            <div className={styles['po-cards']}>
              {activeTab === 'assignments' ? (
                filteredAssignments.length === 0 ? (
                  <div style={{ textAlign: 'center', padding: '40px', color: '#94a3b8' }}>
                    <Clock9 size={48} style={{ color: '#94a3b8', marginBottom: '12px', display: 'inline-block' }} />
                    <p>No overtime assignments scheduled.</p>
                  </div>
                ) : (
                  filteredAssignments.map((assignment) => (
                    <div key={assignment.id} className={styles['po-card']}>
                      <div className={styles['card-main']}>
                        <div className={styles['card-info']}>
                          <span className={styles['card-id']}>OT-{String(assignment.id).padStart(4, '0')}</span>
                          <span className={styles['card-amount']}>
                            {assignment.ot_hours !== null && assignment.ot_hours !== undefined ? `${assignment.ot_hours} hrs` : '—'}
                          </span>
                        </div>
                        <div className={`action-wrapper ${styles['action-wrapper']}`}>
                          {assignment.status?.toLowerCase() === 'assigned' && (
                            <button
                              className={styles['punch-btn-success']}
                              onClick={() => handlePunchAction(assignment, 'in')}
                              disabled={punchingId === assignment.id}
                              style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '6px 12px', borderRadius: '8px', border: 'none', background: '#22c55e', color: 'white', fontWeight: 600, fontSize: '12px' }}
                            >
                              {punchingId === assignment.id ? <Loader2 className={styles['spinner']} size={12} /> : <MapPinned size={12} />}
                              <span>Punch In</span>
                            </button>
                          )}

                          {assignment.status?.toLowerCase() === 'in_progress' && (
                            <button
                              className={styles['punch-btn-danger']}
                              onClick={() => handlePunchAction(assignment, 'out')}
                              disabled={punchingId === assignment.id}
                              style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '6px 12px', borderRadius: '8px', border: 'none', background: '#ef4444', color: 'white', fontWeight: 600, fontSize: '12px' }}
                            >
                              {punchingId === assignment.id ? <Loader2 className={styles['spinner']} size={12} /> : <MapPinned size={12} />}
                              <span>Punch Out</span>
                            </button>
                          )}

                          {assignment.status?.toLowerCase() === 'completed' && (
                            <span className={styles['punch-completed-badge']} style={{ display: 'flex', alignItems: 'center', gap: '4px', color: '#16a34a', fontWeight: 700, fontSize: '12px' }}>
                              <CheckCircle2 size={14} />
                              <span>Completed</span>
                            </span>
                          )}
                        </div>
                      </div>
                      <div className={styles['card-details']}>
                        <div className={styles['detail-item']} style={{ gridColumn: 'span 2' }}>
                          <span className={styles['detail-label']}>Project Site</span>
                          <span className={styles['detail-value']}>{assignment.project_site}</span>
                        </div>
                        <div className={styles['detail-item']}>
                          <span className={styles['detail-label']}>Shift Date</span>
                          <span className={styles['detail-value']}>
                            {assignment.date ? new Date(assignment.date).toLocaleDateString('en-US', { day: '2-digit', month: 'short', year: 'numeric' }) : 'N/A'}
                          </span>
                        </div>
                        <div className={styles['detail-item']}>
                          <span className={styles['detail-label']}>Scheduled Hours</span>
                          <span className={styles['detail-value']}>{to12hr(assignment.expected_start_time)} – {to12hr(assignment.expected_end_time)}</span>
                        </div>
                        <div className={styles['detail-item']} style={{ gridColumn: 'span 2' }}>
                          <span className={styles['detail-label']}>Punches</span>
                          <span className={styles['detail-value']} style={{ fontSize: '12px' }}>
                            In: {assignment.punch_in_at ? new Date(assignment.punch_in_at).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit', hour12: true }) : '—'} |
                            Out: {assignment.punch_out_at ? new Date(assignment.punch_out_at).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit', hour12: true }) : '—'}
                          </span>
                        </div>
                        <div className={styles['detail-item']} style={{ gridColumn: 'span 2' }}>
                          <span className={styles['detail-label']}>Status</span>
                          <span className={`${styles['status-badge']} ${getStatusClass(assignment.status)}`} style={{ minWidth: 'auto', fontSize: '10px', padding: '4px 8px', width: 'fit-content' }}>
                            {getStatusIcon(assignment.status)}
                            <span style={{ marginLeft: '4px' }}>{getStatusLabel(assignment.status)}</span>
                          </span>
                        </div>
                        {assignment.instructions && assignment.instructions !== 'N/A' && (
                          <div className={styles['detail-item']} style={{ gridColumn: 'span 2' }}>
                            <span className={styles['detail-label']}>Instructions</span>
                            <span className={styles['detail-value']} style={{ color: '#64748b', fontStyle: 'italic' }}>"{assignment.instructions}"</span>
                          </div>
                        )}
                      </div>
                      <div className={styles['card-footer']}>
                        <div className={styles['footer-left']}>
                          <span>Assigned by: {assignment.assigned_by}</span>
                        </div>
                      </div>
                    </div>
                  ))
                )
              ) : (
                /* Requests Mobile Cards */
                filteredRequests.length === 0 ? (
                  <div style={{ textAlign: 'center', padding: '40px', color: '#94a3b8' }}>
                    <Calendar size={48} style={{ color: '#94a3b8', marginBottom: '12px', display: 'inline-block' }} />
                    <p>No overtime requests raised.</p>
                  </div>
                ) : (
                  filteredRequests.map((req) => (
                    <div key={req.id} className={styles['po-card']}>
                      <div className={styles['card-main']}>
                        <div className={styles['card-info']}>
                          <span className={styles['card-id']}>REQ-{String(req.id).padStart(4, '0')}</span>
                          <span className={styles['card-amount']}>{req.project_site}</span>
                        </div>
                        <div className={`action-wrapper ${styles['action-wrapper']}`}>
                          <span className={`${styles['status-badge']} ${getStatusClass(req.status)}`} style={{ minWidth: 'auto', fontSize: '10px', padding: '4px 8px' }}>
                            {getStatusIcon(req.status)}
                            <span style={{ marginLeft: '4px' }}>{getStatusLabel(req.status)}</span>
                          </span>
                        </div>
                      </div>
                      <div className={styles['card-details']}>
                        <div className={styles['detail-item']}>
                          <span className={styles['detail-label']}>Request Date</span>
                          <span className={styles['detail-value']}>
                            {req.date ? new Date(req.date).toLocaleDateString('en-US', { day: '2-digit', month: 'short', year: 'numeric' }) : 'N/A'}
                          </span>
                        </div>
                        <div className={styles['detail-item']}>
                          <span className={styles['detail-label']}>Requested Timings</span>
                          <span className={styles['detail-value']}>{req.requested_start_time} - {req.requested_end_time}</span>
                        </div>
                        <div className={styles['detail-item']} style={{ gridColumn: 'span 2' }}>
                          <span className={styles['detail-label']}>Reason</span>
                          <span className={styles['detail-value']}>{req.reason}</span>
                        </div>
                        {req.approval_remarks && (
                          <div className={styles['detail-item']} style={{ gridColumn: 'span 2' }}>
                            <span className={styles['detail-label']}>Review Remarks</span>
                            <span className={styles['detail-value']} style={{ color: '#64748b' }}>{req.approval_remarks}</span>
                          </div>
                        )}
                      </div>
                    </div>
                  ))
                )
              )}
            </div>

            <div className={styles['desktop-pagination']}>
              <Pagination
                currentPage={paginationData.current_page}
                totalItems={paginationData.total}
                itemsPerPage={paginationData.per_page}
                onPageChange={(page) => { setCurrentPage(page); }}
              />
            </div>
          </div>

          <div className={styles['mobile-pagination']}>
            <Pagination
              currentPage={paginationData.current_page}
              totalItems={paginationData.total}
              itemsPerPage={paginationData.per_page}
              onPageChange={(page) => { setCurrentPage(page); }}
            />
          </div>

          <Footer />
        </main>
      </div >

      <OTRequestDrawer
        isOpen={isRequestDrawerOpen}
        onClose={() => setIsRequestDrawerOpen(false)}
        onSave={() => {
          if (activeTab === 'requests') {
            fetchRequests();
          } else {
            setActiveTab('requests');
            fetchRequests();
          }
        }}
      />
    </div >
  );
};

export default EmployeeOT;


